import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { PricingSection } from "./Pricing";
import { LeadMagnetSection } from "../components/LeadMagnetSection";
import { ExitIntentPopup } from "../components/ExitIntentPopup";
import { ProgressionMap } from "../components/ProgressionMap";
import { SocialProof } from "../components/SocialProof";
import { WhyPay } from "../components/WhyPay";
import { LandingFAQ } from "../components/LandingFAQ";
import { useSEO } from "../hooks/useSEO";
import { getNextJLPT, jlptDateShort, jlptDateLong } from "../lib/jlpt";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Check, Zap, Brain, MessageCircle, Trophy, BookOpen, Play } from "lucide-react";

const DARUMA = "https://static.prod-images.emergentagent.com/jobs/6aac7739-0d83-4999-87fa-93aae3a1400b/images/14f34c12bb71d430ec756ab3328fec1b2292cdadf2dee1d9d4f80344385b08a3.png";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/6aac7739-0d83-4999-87fa-93aae3a1400b/images/1e19f399591f4f14fff743176f3e81834bfefa4e589ffab3f5ad036b4b926ea2.png";
const ATMO_1 = "/images/couple-torii.jpg";
const ATMO_2 = "/images/couple-train.webp";

const TICKER = ["JLPT N5", "200 leçons N5→N3", "Examens blancs", "Tuteur IA 24/7", "15 min/jour", "Audio natif", "En français", "Vocabulaire N5-N3"];

const Landing = () => {
  const [email, setEmail] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [plans, setPlans] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const nextJlpt = getNextJLPT();

  useSEO({
    title: "Japonais Pas à Pas : Préparez et réussissez le JLPT N5, la méthode française pas à pas",
    description:
      "Préparation JLPT N5 en français : 200 leçons du N5 au N3, examens blancs chronométrés, tuteur IA et audio natif. 15 minutes par jour. Essai gratuit 3 jours sans carte bancaire.",
    canonical: "https://japonais-pas-a-pas.com/",
  });

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(r.data.data)).catch(() => {});
  }, []);

  const startCheckout = async (planId, promoOverride) => {
    if (!user) {
      toast.info("Créez votre compte (essai 3j gratuit) puis revenez ici.");
      nav("/register");
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
        promo_code: promoOverride || undefined,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoadingPlan(null);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="washi-bg min-h-screen" data-testid="landing-page">
      <Navbar />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 pb-24 sm:pt-24 sm:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-[#FFD166]/30 border border-[#FFD166] rounded-full px-4 py-1.5 mb-8" data-testid="hero-badge">
              <Sparkles className="w-3.5 h-3.5 text-[#1A2A40]" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#1A2A40]">PROCHAIN JLPT · {jlptDateLong(nextJlpt)} · OBJECTIF N5</span>
            </div>

            <h1
              className="font-display font-black text-[clamp(2.25rem,5.2vw,4.5rem)] leading-[0.95] tracking-tighter text-[#1A2A40] mb-4 hyphens-none [word-break:normal] [overflow-wrap:normal]"
              lang="fr"
            >
              <span className="whitespace-nowrap">Le <span className="text-[#E94A3F]">JLPT{"\u00A0"}N5</span>, c'est le</span>
              <br />
              <span className="whitespace-nowrap"><span className="text-[#E94A3F]">{jlptDateShort(nextJlpt)}</span>.</span>
              <br />
              <span className="whitespace-nowrap">Serez{"\u2011"}vous vraiment</span>
              <br />
              <span className="whitespace-nowrap">prêt{"\u00A0"}?</span>
            </h1>

            <p className="italic text-lg sm:text-xl text-[#4A5568] mb-6" data-testid="hero-subtitle">
              Ne découvrez pas vos lacunes le jour de l'examen.
            </p>

            <p className="text-base sm:text-lg text-[#4A5568] max-w-xl mb-10 leading-relaxed">
              Arrêtez de réviser au hasard. Suivez un parcours structuré en français avec 200 leçons,
              examens blancs chronométrés, audio natif et tuteur IA pour savoir exactement quoi travailler
              avant le jour J. 15 minutes par jour.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="bg-[#E94A3F] text-white font-bold py-4 px-7 rounded-xl shadow-brut hover-lift inline-flex items-center justify-center gap-2 text-base"
              >
                Je veux être prêt pour le {jlptDateShort(nextJlpt)} <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/placement"
                data-testid="hero-cta-secondary"
                className="bg-[#FDFBF7] border-2 border-[#1A2A40] text-[#1A2A40] font-bold py-4 px-7 rounded-xl hover-lift inline-flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" /> Test de niveau (2 min)
              </Link>
            </div>

            <div className="flex items-center gap-6 text-sm text-[#4A5568]">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#8CA07A]" /> Essai 3 jours offert</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#8CA07A]" /> 100% en français</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#8CA07A]" /> Examens blancs inclus</div>
            </div>
          </div>

          {/* Hero visual: bento */}
          <div className="lg:col-span-5 relative" data-testid="hero-visual">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-brut-lg border-2 border-[#1A2A40]">
              <img src={HERO_BG} alt="Intérieur japonais minimaliste" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2A40]/40 via-transparent to-transparent" />
              <div className="absolute top-6 right-6 bg-[#FDFBF7] rounded-2xl px-4 py-3 shadow-brut-sm rotate-3">
                <div className="text-[10px] uppercase font-bold tracking-widest text-[#E94A3F]">Aujourd'hui</div>
                <div className="font-display font-black text-2xl text-[#1A2A40]">15 min</div>
              </div>
              <div className="absolute bottom-6 left-6 right-6 bg-[#FDFBF7] rounded-2xl p-5 shadow-brut">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-[#FFD166] flex items-center justify-center text-2xl font-jp font-bold">あ</div>
                  <div className="flex-1">
                    <div className="text-xs font-bold text-[#E94A3F] uppercase tracking-wider">Leçon 1</div>
                    <div className="font-display font-bold text-[#1A2A40]">Hiragana : la voyelle « A »</div>
                  </div>
                </div>
                <div className="mt-3 h-2 bg-[#F3F6F1] rounded-full overflow-hidden">
                  <div className="h-full bg-[#8CA07A] w-[68%] rounded-full" />
                </div>
              </div>
            </div>

            <div className="absolute -top-6 -left-6 w-24 h-24 hidden lg:block animate-float">
              <img src={DARUMA} alt="Daruma" className="w-full h-full object-contain drop-shadow-xl" />
            </div>
          </div>
        </div>

        {/* Marquee ticker */}
        <div className="border-y-2 border-[#1A2A40] bg-[#1A2A40] text-[#FDFBF7] overflow-hidden py-4">
          <div className="flex gap-12 animate-marquee whitespace-nowrap">
            {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
              <span key={i} className="font-display font-bold text-lg flex items-center gap-12">
                {t} <span className="text-[#E94A3F]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS / SOCIAL PROOF ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20" data-testid="stats-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { k: "200", v: "leçons N5 → N3" },
            { k: "636", v: "mots de vocabulaire" },
            { k: "15 min", v: "par jour suffisent" },
            { k: "100%", v: "français" },
          ].map((s, i) => (
            <div key={i} className="text-center" data-testid={`stat-${i}`}>
              <div className="font-display font-black text-4xl sm:text-5xl text-[#E94A3F] mb-2">{s.k}</div>
              <div className="text-sm text-[#4A5568] uppercase tracking-wider">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ BENTO FEATURES ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20" data-testid="features-section">
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">La méthode</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40]">
              Tout ce qu'il vous faut.<br /><span className="italic font-light">Rien de superflu.</span>
            </h2>
          </div>
          <p className="lg:col-span-2 text-lg text-[#4A5568] leading-relaxed self-end">
            On a découpé le N5 en micro-leçons, mémo-tests espacés et exercices générés
            sur mesure par une IA. Le jour de l'examen, rien ne vous surprendra.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Big tile: AI Tutor */}
          <div className="md:col-span-7 bg-[#1A2A40] text-[#FDFBF7] rounded-3xl p-8 sm:p-12 shadow-brut border-2 border-[#1A2A40] relative overflow-hidden" data-testid="feature-ai">
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#E94A3F]/30 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 bg-[#E94A3F] rounded-full px-3 py-1 mb-6">
                <Brain className="w-3.5 h-3.5" />
                <span className="text-xs font-bold uppercase tracking-widest">Sensei IA</span>
              </div>
              <h3 className="font-display font-black text-3xl sm:text-4xl mb-4">Un tuteur disponible 24/7.</h3>
              <p className="text-[#FDFBF7]/80 mb-6 max-w-md leading-relaxed">
                Posez n'importe quelle question : grammaire, kanji, expression idiomatique. Sensei répond
                en français avec exemples, romaji et traduction.
              </p>
              <div className="bg-[#FDFBF7]/10 backdrop-blur-md border border-[#FDFBF7]/20 rounded-2xl p-5 space-y-3 max-w-md">
                <div className="text-sm">
                  <span className="text-[#FFD166] font-bold">Vous : </span>
                  Quelle différence entre は et が ?
                </div>
                <div className="text-sm bg-[#8CA07A]/20 rounded-xl p-3 border border-[#8CA07A]/40">
                  <span className="text-[#FFD166] font-bold">Sensei : </span>
                  「は」 marque le thème (déjà connu), 「が」 introduit un sujet nouveau. Ex : 私<b>は</b>学生です. Comprenez-vous la nuance ?
                </div>
              </div>
            </div>
          </div>

          {/* Kana tile */}
          <Link
            to="/kana"
            className="md:col-span-5 bg-[#FFD166] rounded-3xl p-8 shadow-brut border-2 border-[#1A2A40] hover-lift block group cursor-pointer"
            data-testid="feature-kana"
          >
            <div className="flex items-start justify-between mb-4">
              <BookOpen className="w-7 h-7" />
              <span className="text-xs font-bold uppercase tracking-widest bg-[#1A2A40] text-[#FDFBF7] rounded-full px-3 py-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Essayer →
              </span>
            </div>
            <h3 className="font-display font-black text-2xl mb-3 group-hover:underline decoration-2 underline-offset-4">Maîtrisez les kana.</h3>
            <p className="text-[#1A2A40]/80 mb-6">46 hiragana, 46 katakana. Interactifs, audio, mémorisation espacée.</p>
            <div className="grid grid-cols-5 gap-2">
              {["あ","い","う","え","お","か","き","く","け","こ"].map((k) => (
                <div key={k} className="bg-[#FDFBF7] rounded-lg aspect-square flex items-center justify-center font-jp font-bold text-xl shadow-brut-sm group-hover:bg-[#E94A3F] group-hover:text-white transition-all">
                  {k}
                </div>
              ))}
            </div>
          </Link>

          {/* Lessons */}
          <div className="md:col-span-4 bg-[#F3F6F1] rounded-3xl p-8 border-2 border-[#1A2A40]/10 hover-lift" data-testid="feature-lessons">
            <Zap className="w-7 h-7 text-[#E94A3F] mb-4" />
            <h3 className="font-display font-black text-2xl mb-3">Micro-leçons de 7 min.</h3>
            <p className="text-[#4A5568]">Conçues pour le métro, la pause café, le canapé du soir. Vocabulaire, grammaire, audio.</p>
          </div>

          {/* Quiz */}
          <div className="md:col-span-4 bg-[#FDFBF7] rounded-3xl p-8 border-2 border-[#1A2A40]/10 hover-lift" data-testid="feature-quiz">
            <Trophy className="w-7 h-7 text-[#8CA07A] mb-4" />
            <h3 className="font-display font-black text-2xl mb-3">Test de niveau JLPT.</h3>
            <p className="text-[#4A5568]">En 2 minutes, on positionne votre niveau (N5 → N3) et on construit votre parcours.</p>
          </div>

          {/* Chat practice */}
          <div className="md:col-span-4 bg-[#E94A3F] text-white rounded-3xl p-8 shadow-brut border-2 border-[#1A2A40] hover-lift" data-testid="feature-practice">
            <MessageCircle className="w-7 h-7 mb-4" />
            <h3 className="font-display font-black text-2xl mb-3">Exercices à l'infini.</h3>
            <p className="text-white/90">L'IA génère des exercices personnalisés à votre niveau. Jamais deux identiques.</p>
          </div>
        </div>
      </section>

      {/* ============ PROGRESSION MAP — 12 themes / 200 lessons ============ */}
      <ProgressionMap />

      {/* ============ HOW IT WORKS — editorial 3-step ============ */}
      <section className="bg-[#1A2A40] text-[#FDFBF7] py-24" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFD166] mb-3">Trois étapes</div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1]">
                De zéro au <span className="font-jp text-[#E94A3F]">JLPT</span>,<br />sans souffrir.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-[#FDFBF7]/70 leading-relaxed">
                Pas de promesse fantaisiste. Une progression rigoureuse, des habitudes minuscules,
                et des résultats qui s'accumulent.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Évaluez votre niveau", d: "10 questions, 2 minutes. On calibre votre point de départ vers le JLPT, quel que soit votre niveau visé.", c: "#FFD166" },
              { n: "02", t: "Suivez votre parcours", d: "Une leçon par jour, validée par mémo-tests espacés et par un quiz à la fin de chaque leçon.", c: "#E94A3F" },
              { n: "03", t: "Arrivez prêt le jour de l'examen", d: "Progressez leçon après leçon vers votre JLPT, et pratiquez chaque concept avec votre tuteur IA, sans peur, sans jugement.", c: "#8CA07A" },
            ].map((s, i) => (
              <div key={i} className="bg-[#FDFBF7]/5 backdrop-blur-md border border-[#FDFBF7]/10 rounded-3xl p-8" data-testid={`step-${i+1}`}>
                <div className="font-display font-black text-6xl mb-6" style={{ color: s.c }}>{s.n}</div>
                <h3 className="font-display font-black text-2xl mb-3">{s.t}</h3>
                <p className="text-[#FDFBF7]/70 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EDITORIAL ATMO ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-24" data-testid="culture-section">
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Derrière Japonais Pas à Pas</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] mb-6 text-[#1A2A40]">
              Un couple <span className="italic font-light">franco-japonais</span>, pas un algorithme.
            </h2>
            <p className="text-lg text-[#4A5568] mb-8 leading-relaxed">
              Japonais Pas à Pas est né d'une histoire vraie. Alexis et Atsuko se sont rencontrés au Japon en 2001, mariés depuis vingt ans, ensemble depuis vingt-cinq. Alexis a vécu plus de vingt ans au Japon, Atsuko est japonaise. C'est elle qui veille à ce que la langue enseignée et les voix soient justes et naturelles, jamais approximatives. Ici, vous n'apprenez pas avec un contenu générique fabriqué par une machine. Vous apprenez avec des gens qui connaissent le Japon de l'intérieur, et qui l'aiment.
            </p>
            <Link to="/register" data-testid="culture-cta" className="inline-flex items-center gap-2 font-bold text-[#E94A3F] hover:gap-3 transition-all">
              Rejoindre l'aventure <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            <figure className="m-0">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden border-2 border-[#1A2A40] shadow-brut">
                <img src={ATMO_1} alt="Atsuko et Alexis devant le torii de Miyajima au Japon" className="w-full h-full object-cover" />
              </div>
              <figcaption className="mt-3 text-sm text-[#4A5568] italic text-center" data-testid="culture-caption-1">
                Atsuko et Alexis à Miyajima, octobre 2006.
              </figcaption>
            </figure>
            <figure className="m-0 translate-y-12">
              <div className="aspect-[3/4] rounded-3xl overflow-hidden border-2 border-[#1A2A40] shadow-brut">
                <img src={ATMO_2} alt="Atsuko et Alexis dans un train au Japon" className="w-full h-full object-cover" />
              </div>
              <figcaption className="mt-3 text-sm text-[#4A5568] italic text-center" data-testid="culture-caption-2">
                De retour au Japon, juillet 2026.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* ============ LEAD MAGNET PDF ============ */}
      <LeadMagnetSection />

      {/* ============ WHY PAY vs FREE APPS / AI ============ */}
      <WhyPay />

      {/* ============ SOCIAL PROOF (rends null tant qu'il n'y a aucun avis) ============ */}
      <SocialProof />

      {/* ============ PRICING ============ */}
      <PricingSection plans={plans} onStart={startCheckout} loadingPlan={loadingPlan} user={user} />

      {/* ============ FAQ ============ */}
      <LandingFAQ />

      {/* ============ NEWSLETTER ============ */}
      <section className="max-w-5xl mx-auto px-6 sm:px-12 py-24" data-testid="newsletter-section">
        <div className="bg-[#E94A3F] text-white rounded-3xl p-10 sm:p-16 shadow-brut-lg border-2 border-[#1A2A40] relative overflow-hidden">
          <div className="absolute -right-10 -top-10 font-jp font-black text-[14rem] text-white/10 leading-none select-none">朱</div>
          <div className="relative max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFD166] mb-3">Lettre du dimanche</div>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter leading-[1] mb-4">
              1 leçon gratuite chaque dimanche.
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Une expression, un kanji, une anecdote culturelle. Désinscription en 1 clic.
            </p>
            <form onSubmit={submitNewsletter} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                data-testid="newsletter-email-input"
                className="flex-1 bg-white text-[#1A2A40] rounded-xl px-5 py-4 font-medium placeholder:text-[#A0AEC0] focus:outline-none focus:ring-4 focus:ring-[#FFD166]"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="newsletter-submit-btn"
                className="bg-[#1A2A40] text-white font-bold py-4 px-6 rounded-xl hover:bg-black transition-colors disabled:opacity-60"
              >
                {submitting ? "..." : "Je m'abonne"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-24 text-center" data-testid="final-cta">
        <h2 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-[1] text-[#1A2A40] mb-6">
          Et si vous commenciez<br /><span className="font-jp text-[#E94A3F]">今日 ?</span> <span className="italic font-light">aujourd'hui ?</span>
        </h2>
        <p className="text-lg text-[#4A5568] mb-10 max-w-xl mx-auto">
          La première leçon est gratuite. Sans engagement. Promis.
        </p>
        <Link
          to="/register"
          data-testid="final-cta-btn"
          className="inline-flex items-center gap-2 bg-[#E94A3F] text-white font-bold py-5 px-10 rounded-xl shadow-brut hover-lift text-lg"
        >
          Démarrer ma première leçon <ArrowRight className="w-5 h-5" />
        </Link>
      </section>

      {/* ============ SEO TEXTUAL CONTENT (long-form, keyword-rich) ============ */}
      <section className="bg-[#FDFBF7] border-t border-[#1A2A40]/10 py-20" data-testid="seo-content">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 prose prose-slate">
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#1A2A40] leading-tight mb-6">
            Préparer le <span className="text-[#E94A3F]">JLPT N5</span> avec Japonais Pas à Pas
          </h2>

          <p className="text-[#4A5568] leading-relaxed mb-5">
            Vous voulez <strong>passer et réussir le JLPT N5</strong> (Japanese Language Proficiency Test,
            niveau débutant officiel) ? Japonais Pas à Pas est une méthode complète de préparation
            <strong> 100% en français</strong>, du niveau grand débutant jusqu'au N3 intermédiaire.
          </p>

          <p className="text-[#4A5568] leading-relaxed mb-8">
            Notre principe : <strong>15 minutes par jour</strong>, tous les jours. Un parcours linéaire de
            200 leçons, des exercices immédiats après chaque notion, un tuteur IA en français pour lever
            les doutes et des examens blancs chronométrés au format officiel du JLPT.
          </p>

          <h3 className="font-display font-bold text-2xl text-[#1A2A40] mb-3">Le programme officiel du JLPT N5 couvert</h3>
          <ul className="text-[#4A5568] leading-relaxed mb-8 space-y-2">
            <li><strong>Hiragana et katakana</strong> : les 92 syllabaires enseignés dès la première quinzaine</li>
            <li><strong>~100 kanji N5</strong> : chaque kanji introduit avec ses lectures on/kun et 2 mots d'exemple</li>
            <li><strong>~800 mots de vocabulaire N5</strong> : classés par thème et retravaillés en révision espacée (SRS)</li>
            <li><strong>Grammaire N5</strong> : です/ます, particules は/が/を/に, adjectifs, temps de base</li>
            <li><strong>Compréhension écrite et orale</strong> : audio natif intégré à chaque exercice</li>
            <li><strong>Examens blancs JLPT N5</strong> chronométrés au format officiel Japan Foundation</li>
          </ul>

          <h3 className="font-display font-bold text-2xl text-[#1A2A40] mb-3">Comment se déroule la préparation ?</h3>
          <p className="text-[#4A5568] leading-relaxed mb-5">
            Vous commencez par un test de niveau gratuit qui situe votre point de départ. Ensuite, chaque
            leçon suit la même structure : 1 notion introduite en français, 5-8 exercices interactifs avec
            audio, puis un mini-quiz pour ancrer. Le système de révision (SRS) fait remonter automatiquement
            les kanji et mots que vous êtes en train d'oublier, au bon moment, avant qu'ils ne se perdent.
          </p>

          <p className="text-[#4A5568] leading-relaxed mb-8">
            <strong>Essai gratuit de 3 jours</strong> sans carte bancaire. Vous accédez immédiatement aux leçons
            N5, au tuteur IA, aux exercices et au premier examen blanc pour vous faire une idée précise
            avant tout engagement.
          </p>

          <h3 className="font-display font-bold text-2xl text-[#1A2A40] mb-3">Combien de temps pour se sentir prêt au JLPT N5 ?</h3>
          <p className="text-[#4A5568] leading-relaxed mb-5">
            Cela dépend uniquement de votre régularité. Le programme officiel du N5 représente environ
            100 kanji, 800 mots et une trentaine de points de grammaire, c'est du travail réel. Avec
            15 minutes par jour et sans coupure, la plupart des étudiants motivés se sentent prêts à
            passer le test en quelques mois. Nous fournissons le parcours ; la régularité vient de vous.
          </p>

          <p className="text-[#4A5568] leading-relaxed mb-3">
            Notre positionnement est simple : <strong>la régularité l'emporte sur l'intensité</strong>.
            Mieux vaut 15 minutes tous les jours pendant six mois qu'une session marathon une fois par mois.
            Le tableau de bord vous indique où vous en êtes, chapitre par chapitre.
          </p>
        </div>
      </section>

      <Footer />
      <ExitIntentPopup />
    </div>
  );
};

export default Landing;
